import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxPopupModule } from 'devextreme-angular/ui/popup';
import { DxTextAreaComponent, DxTextAreaModule } from 'devextreme-angular/ui/text-area';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxTreeViewModule } from 'devextreme-angular/ui/tree-view';
import {
  ExpressionSuggestionItem,
  ExpressionTool,
  ExpressionToolCategory,
  FieldOption,
  MappingGridRow,
  MappingPicklistOption,
  TreeNode
} from '../../models/link-generic-mapper.models';

@Component({
  selector: 'app-expression-editor-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DxPopupModule,
    DxButtonModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxTreeViewModule
  ],
  templateUrl: './expression-editor-popup.component.html',
  styleUrl: './expression-editor-popup.component.scss'
})
export class ExpressionEditorPopupComponent implements OnChanges {
	expressionFieldTab: 'source' | 'target' = 'source';
	readonly expressionDraftElementAttr = { 'data-cy': 'mapper-expression-draft' };
	readonly applyExpressionButtonElementAttr = { 'data-cy': 'mapper-apply-expression-button' };

  readonly exampleJoinWithSpace =
  "Upper([billToCountryID.Name]) + ' ' + Lower([countryID.InvoiceTemplateID.Name])";

readonly exampleAddSeparator =
  "[CompanyName] + ' - ' + [AccountCode]";

readonly exampleFallbackValue =
  "Iif(IsNull([CompanyName]), 'Unknown', [CompanyName])";

readonly exampleWrapInParentheses =
  "[CompanyName] + ' (' + [AccountCode] + ')'";

  @Input() visible = false;
  @Input() draft = '';

  @Input() sourceTreeItems: TreeNode[] = [];
  @Input() targetTreeItems: TreeNode[] = [];
  @Input() sourceSystemName = 'Source';
  @Input() targetSystemName = 'Target';
  @Input() selectedSourceField: FieldOption | null = null;
  @Input() editingRow: MappingGridRow | null = null;
  @Input() fieldSearch = '';
  @Input() targetFieldSearch = '';
  @Input() showTargetFields = false;

  @Input() expressionToolSearch = '';
  @Input() selectedExpressionToolCategory: ExpressionToolCategory | 'All' = 'Favorites';
  @Input() expressionToolCategories: Array<ExpressionToolCategory | 'All'> = [];
  @Input() groupedExpressionTools: Array<{ category: string; items: ExpressionTool[] }> = [];
  @Input() expressionSuggestionsVisible = false;
  @Input() expressionSuggestions: ExpressionSuggestionItem[] = [];
  @Input() expressionSuggestionIndex = 0;

  @Input() tipsExpanded = false;
	@Input() canTestExpression = false;
	@Input() expressionTestLoading = false;
	@Input() expressionTestRan = false;
	@Input() expressionTestIsValid: boolean | null = null;
	@Input() expressionTestHasResult = false;
	@Input() expressionTestResult: any = null;
	@Input() expressionTestMessage: string | null = null;
	@Input() expressionTestUsedValues: Array<{ path: string; label: string; value: string }> = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() draftChange = new EventEmitter<string>();
  @Output() fieldSearchChange = new EventEmitter<string>();
  @Output() targetFieldSearchChange = new EventEmitter<string>();
  @Output() expressionToolSearchChange = new EventEmitter<string>();
  @Output() selectedExpressionToolCategoryChange = new EventEmitter<ExpressionToolCategory | 'All'>();

  @Output() cancel = new EventEmitter<void>();
  @Output() apply = new EventEmitter<void>();
  @Output() editorInitialized = new EventEmitter<any>();
  @Output() editorInput = new EventEmitter<void>();
  @Output() editorFocusIn = new EventEmitter<void>();
  @Output() editorKeyDown = new EventEmitter<any>();
  @Output() applySuggestion = new EventEmitter<ExpressionSuggestionItem>();
  @Output() treeFieldSelected = new EventEmitter<TreeNode>();
	@Output() deferredSourceNodeExpanded = new EventEmitter<TreeNode>();
  @Output() targetTreeFieldSelected = new EventEmitter<TreeNode>();
  @Output() picklistFieldSelected = new EventEmitter<TreeNode>();
  @Output() insertTool = new EventEmitter<ExpressionTool>();
  @Output() insertPicklistValue = new EventEmitter<MappingPicklistOption>();
  @Output() insertPicklistComparison = new EventEmitter<MappingPicklistOption>();
	@Output() testExpression = new EventEmitter<void>();

  @Output() tipsExpandedChange = new EventEmitter<boolean>();
  @Output() insertExample = new EventEmitter<string>();

  @ViewChild(DxTextAreaComponent) expressionEditor?: DxTextAreaComponent;

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['showTargetFields'] && !this.showTargetFields) {
			this.expressionFieldTab = 'source';
		}
	}

  toggleTips(): void {
    this.tipsExpandedChange.emit(!this.tipsExpanded);
  }

  onExampleClick(example: string): void {
    this.insertExample.emit(example);
  }

	onPicklistChipClick(event: MouseEvent, item: TreeNode): void {
		event.preventDefault();
		event.stopPropagation();
		this.picklistFieldSelected.emit(item);
	}

	onSourceTreeItemClick(event: MouseEvent, item: TreeNode): void {
		if (item.childrenDeferred) {
			this.deferredSourceNodeExpanded.emit(item);
			return;
		}

		if (item.items?.length) {
			return;
		}

		event.stopPropagation();
		this.treeFieldSelected.emit(item);
	}

	get selectedFieldPicklistOptions(): MappingPicklistOption[] {
		if (!this.selectedSourceField?.isPicklist) {
			return [];
		}

		return (this.selectedSourceField.picklistOptions ?? []).filter(option => option.isActive !== false);
	}

	formatExpressionTestResult(value: any): string {
		if (value == null) {
			return 'null';
		}

		if (typeof value === 'object') {
			try {
				return JSON.stringify(value, null, 2);
			} catch {
				return '[object]';
			}
		}

		return String(value);
	}

	getEditingFieldTitle(): string {
		return this.editingRow?.targetDisplayName || this.editingRow?.targetPath || 'Expression';
	}

	getEditingFieldPath(): string | null {
		const path = this.editingRow?.targetPath?.trim();
		return path && path !== this.getEditingFieldTitle() ? path : null;
	}

	getEditingFieldDescription(): string {
		return this.editingRow?.description?.trim() || 'No field description available.';
	}

	getEditingFieldDetails(): string {
		if (!this.editingRow) {
			return '';
		}

		const parts = [
			this.editingRow.targetDataType,
			this.editingRow.required ? 'required' : null,
			this.editingRow.readOnly ? 'read-only' : null,
			this.editingRow.isPicklist ? 'picklist' : null,
			this.editingRow.maxLength ? `max length ${this.editingRow.maxLength}` : null
		].filter(Boolean);

		return parts.join(' · ');
	}

	setExpressionFieldTab(tab: 'source' | 'target'): void {
		if (tab === 'target' && !this.showTargetFields) {
			return;
		}

		this.expressionFieldTab = tab;
	}
}
